import { gql, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { object, string } from 'yup'

import { useUser } from 'store'
import { toast } from 'components/global/CUK/Toast/Toast'
import { CIVILITIES, COUNTRY } from 'pages/Contacts/Edit/constants'

export const OPERATION_UPDATE_PROFILE = gql`
  mutation UpdateProfile(
    $salutation: String
    $first_name: String
    $last_name: String
    $title: String
    $country: String
    $phone: String
    $mobile_phone: String
  ) {
    profile: updateProfile(
      salutation: $salutation
      first_name: $first_name
      last_name: $last_name
      title: $title
      country: $country
      phone: $phone
      mobile_phone: $mobile_phone
    ) {
      first_name
      last_name
      title
      salutation
      country
      phone
      mobile_phone
      updated_at
    }
  }
`

export interface ProfileFormValues {
  first_name: string
  last_name: string
  title: string
  country: string
  phone: string
  salutation?: string
  mobile_phone?: string
}

const PHONE_PATTERN = /^\+?[\s0-9]*$/
const CIVILITY_VALUES = CIVILITIES.map(({ value }) => value)
const COUNTRY_VALUES = COUNTRY.map(({ value }) => value)

export const buildProfileSchema = ({ t }: { t: TFunction }) => {
  return object({
    salutation: string().oneOf(CIVILITY_VALUES, t('ui_elements.invalid_format_field')),
    first_name: string().trim().required(t('ui_elements.required_field')),
    last_name: string().trim().required(t('ui_elements.required_field')),
    title: string().trim(),
    country: string().oneOf(COUNTRY_VALUES, t('ui_elements.invalid_format_field')),
    phone: string().matches(PHONE_PATTERN, t('ui_elements.invalid_format_field')),
    mobile_phone: string().matches(PHONE_PATTERN, t('ui_elements.invalid_format_field'))
  })
}

const useUpdateProfile = ({ onSuccess }: { onSuccess: () => void }) => {
  const { t } = useTranslation()
  const [, setUser] = useUser()
  const [mutate, { loading }] = useMutation(OPERATION_UPDATE_PROFILE)

  const updateProfile = async (values: ProfileFormValues) => {
    try {
      await mutate({ variables: values })
      setUser(prev =>
        prev
          ? {
              ...prev,
              ...values,
              updated_at: new Date().toISOString()
            }
          : prev
      )
      toast.success(t('profile.edit.toast_success'))
      onSuccess()
    } catch (error) {
      toast.error(t('profile.edit.toast_error'))
    }
  }

  return { updateProfile, loading }
}

export default useUpdateProfile
